import { showToast, toast } from "./toast";

// Delete the way mail apps do. The row has already slid away, so `remove`
// takes it out of the list at once; a toast offers Undo for a few seconds;
// then the server delete runs. Undo puts the row back through `restore` and
// nothing is sent. A failed delete puts the row back too.
//
// The delete is never lost while the window is open: leaving the page (a
// refresh, a navigation, a backgrounded tab) commits it at once with a
// keepalive request the browser finishes through the unload, and `request`
// is kept in a small ledger until the server answers, so the next launch
// replays anything the browser cut off (a killed app, say). Lists filter the
// ledger's ids out of fresh rows (withoutPendingDeletes), so a refresh or a
// live refetch inside the window never paints the row again.
//
//   deleteWithUndo({ noun, ids, remove, restore, request: { url, method, headers, body }, after })
//
// `request` is the server delete (method defaults to DELETE; headers a plain
// object; body a string). `after` runs once it succeeded (a refetch, say).
// `doDelete` is the older form: a function that performs the delete itself
// (nothing is recorded for a replay then).
// `message` replaces the toast's "<Noun> deleted" when the action is not a delete.
export function deleteWithUndo({ noun = "item", message, ids = [], remove, restore, request, doDelete, after, seconds = 4 }) {
  const entry = request ? addPending({ ids, ...request }) : null;
  remove();
  let settled = false;
  const settle = () => {
    settled = true;
    clearTimeout(timer);
    document.removeEventListener("visibilitychange", onHide);
    window.removeEventListener("pagehide", onLeave);
  };
  const fire = async () => {
    if (settled) return;
    settle();
    try {
      if (request) await sendDelete(request, noun);
      else await doDelete?.();
      if (entry) dropPending(entry.key);
      await after?.();
    } catch (err) {
      if (entry) dropPending(entry.key); // the row is back on screen, so nothing may delete it later
      restore();
      toast.error(err?.message || `Could not delete the ${noun}`);
    }
  };
  const undo = () => {
    if (settled) return;
    settle();
    if (entry) dropPending(entry.key);
    restore();
  };
  const onHide = () => { if (document.visibilityState === "hidden") fire(); };
  const onLeave = () => fire();
  const timer = setTimeout(fire, seconds * 1000);
  document.addEventListener("visibilitychange", onHide);
  window.addEventListener("pagehide", onLeave);
  // the update toast's design (the slab that flips with the theme, one text
  // action, no dismiss X), here with Undo as the action
  showToast(message || `${noun.charAt(0).toUpperCase()}${noun.slice(1)} deleted`, {
    duration: seconds * 1000,
    cssClass: "wd-toast wd-toast-invert",
    buttons: [{ text: "Undo", role: "undo", handler: undo }],
  });
}

// The server delete, sent so the browser finishes it even through an unload
async function sendDelete({ url, method = "DELETE", headers, body }, noun) {
  const res = await fetch(url, { method, headers, body, keepalive: true });
  const d = await res.json().catch(() => ({}));
  if (!res.ok || d.success === false) throw new Error(d.detail || `Could not delete the ${noun}`);
  return d;
}

// ── the ledger of deletes still waiting on the server ──
// Local storage, mirrored in memory. An entry lives from the swipe until the
// server has answered (or Undo); whatever is left at the next launch is sent
// again. Every delete route answers a repeat with 404, so a request that did
// land before the page died costs nothing to send twice.
const LEDGER_KEY = "pendingDeletes";
const MAX_AGE = 7 * 24 * 3600 * 1000; // an entry no launch could send in a week is dropped
let ledger = null;
function readLedger() {
  if (ledger) return ledger;
  try { ledger = JSON.parse(localStorage.getItem(LEDGER_KEY) || "[]"); } catch { ledger = []; }
  if (!Array.isArray(ledger)) ledger = [];
  return ledger;
}
function saveLedger() {
  try {
    if (ledger.length) localStorage.setItem(LEDGER_KEY, JSON.stringify(ledger));
    else localStorage.removeItem(LEDGER_KEY);
  } catch { /* storage blocked: the memory copy still guards this session */ }
}
function addPending({ ids, url, method = "DELETE", headers, body }) {
  const entry = { key: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, at: Date.now(), ids: ids.map(String), url, method, headers, body };
  readLedger().push(entry);
  saveLedger();
  return entry;
}
function dropPending(key) {
  ledger = readLedger().filter((e) => e.key !== key);
  saveLedger();
}

// The ids of rows whose delete has not reached the server yet
export function pendingDeleteIds() {
  const pend = readLedger();
  return new Set(pend.length ? pend.flatMap((e) => e.ids || []) : []);
}

// Fresh rows without the ones a swipe is still deleting (a refetch inside the
// Undo window, or the first load after a refresh, would otherwise bring them back)
export function withoutPendingDeletes(rows, key = (r) => r.id) {
  const pend = pendingDeleteIds();
  return pend.size ? rows.filter((r) => !pend.has(String(key(r)))) : rows;
}

// At launch: carry out whatever the last page was still holding
export async function replayPendingDeletes() {
  const now = Date.now();
  const entries = readLedger().slice();
  if (!entries.length) return;
  await Promise.all(entries.map(async (e) => {
    if (now - (e.at || 0) > MAX_AGE) { dropPending(e.key); return; }
    try {
      await fetch(e.url, { method: e.method || "DELETE", headers: e.headers, body: e.body, keepalive: true });
      dropPending(e.key); // any answer settles it (a repeat is a 404)
    } catch { /* offline: the next launch tries again */ }
  }));
}
